/**
 * Fix barcode scans typed while the OS input language is Thai.
 *
 * A barcode scanner emits raw keystrokes. With the Thai (Kedmanee) keyboard layout
 * active, the OS maps each key to its Thai character, so an ASCII barcode like
 * "EA123456785JP|1.5" comes out as Thai gibberish. This maps each character back
 * through the Kedmanee layout to the English key that produced it.
 *
 * Only runs when the string actually contains a Thai character — normal English-mode
 * scans (where "/", "-", "." etc. are literal) are returned untouched.
 */

// Kedmanee output character -> the English/QWERTY key that produced it.
// Includes both unshifted and shifted outputs. No character is a key in both,
// so merging is safe.
const THAI_TO_EN = {
  // number row (unshifted). macOS Thai outputs '_' for the 3-key (Windows outputs '-'),
  // so map both back to '3'. The literal '-' (hyphen) comes from the ข key on both.
  'ๅ': '1', '/': '2', '-': '3', '_': '3', 'ภ': '4', 'ถ': '5', 'ุ': '6', 'ึ': '7', 'ค': '8', 'ต': '9', 'จ': '0', 'ข': '-', 'ช': '=',
  // qwerty row (unshifted)
  'ๆ': 'q', 'ไ': 'w', 'ำ': 'e', 'พ': 'r', 'ะ': 't', 'ั': 'y', 'ี': 'u', 'ร': 'i', 'น': 'o', 'ย': 'p', 'บ': '[', 'ล': ']', 'ฃ': '\\',
  // asdf row (unshifted)
  'ฟ': 'a', 'ห': 's', 'ก': 'd', 'ด': 'f', 'เ': 'g', '้': 'h', '่': 'j', 'า': 'k', 'ส': 'l', 'ว': ';', 'ง': "'",
  // zxcv row (unshifted)
  'ผ': 'z', 'ป': 'x', 'แ': 'c', 'อ': 'v', 'ิ': 'b', 'ื': 'n', 'ท': 'm', 'ม': ',', 'ใ': '.', 'ฝ': '/',
  // number row (shifted)
  '+': '!', '๑': '@', '๒': '#', '๓': '$', '๔': '%', 'ู': '^', '฿': '&', '๕': '*', '๖': '(', '๗': ')', '๘': '_', '๙': '+',
  // qwerty row (shifted)
  '๐': 'Q', '"': 'W', 'ฎ': 'E', 'ฑ': 'R', 'ธ': 'T', 'ํ': 'Y', '๊': 'U', 'ณ': 'I', 'ฯ': 'O', 'ญ': 'P', 'ฐ': '{', 'ฅ': '|',
  // asdf row (shifted)
  'ฤ': 'A', 'ฆ': 'S', 'ฏ': 'D', 'โ': 'F', 'ฌ': 'G', '็': 'H', '๋': 'J', 'ษ': 'K', 'ศ': 'L', 'ซ': ':', '.': '"',
  // zxcv row (shifted)
  '(': 'Z', ')': 'X', 'ฉ': 'C', 'ฮ': 'V', 'ฺ': 'B', '์': 'N', '?': 'M', 'ฒ': '<', 'ฬ': '>', 'ฦ': '?',
  // '}' from shift-] — keep last so it doesn't shadow the ',' consonant above
  ',': '}',
};

const hasThai = (str) => /[฀-๿]/.test(str);

/**
 * If `str` looks like it was typed under a Thai keyboard layout (contains any Thai
 * character), convert every character back to the English key that produced it.
 * Otherwise return it unchanged.
 */
export const normalizeThaiKeyboard = (str) => {
  if (!str || !hasThai(str)) return str;
  return str.replace(/[\s\S]/g, (ch) => (ch in THAI_TO_EN ? THAI_TO_EN[ch] : ch));
};
